import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useExifExtract from "../../hooks/useExifExtract";
import { useAuth } from "../../context/AuthContext";
import { posts as postsApi, upload as uploadApi } from "../../services/api";
import CategoryPicker from "./CategoryPicker";
import CuisinePicker from "./CuisinePicker";
import BatchUploadView from "./BatchUploadView";
import { CATEGORIES } from "../../data/seedData";
import { Aperture, Images, Camera, X, MapPin, Cpu, Clock, Check, Loader, RotateCcw, ThumbsUp } from "lucide-react";
import { isNative, pickPhoto, hapticSuccess } from "../../utils/native";
import { useTranslation } from "react-i18next";

function UploadView() {
  const { t } = useTranslation();

  React.useEffect(() => {
    document.title = `${t("upload.title")} | Pinned`;
    return () => { document.title = t("app.title"); };
  }, [t]);

  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(null);
  const [cuisine, setCuisine] = useState(null);
  const [worthIt, setWorthIt] = useState(null);
  const [wouldReturn, setWouldReturn] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("select"); // "select" | "details" | "batch"
  const { exifData, loading, extractExif, reset } = useExifExtract();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    processFile(file);
  };

  const processFile = async (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
    await extractExif(file);
    setStep("details");
  };

  // Native camera/gallery picker (Capacitor only)
  const handleNativePick = async (source) => {
    try {
      const file = await pickPhoto(source);
      if (file) processFile(file);
    } catch (err) {
      // User cancelled or permission denied — do nothing
    }
  };

  const hasLocation = exifData?.hasGps;

  const getLocation = () => {
    if (exifData?.hasGps) {
      return { latitude: exifData.latitude, longitude: exifData.longitude };
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!hasLocation || !category || !caption.trim() || worthIt === null || wouldReturn === null) return;

    const location = getLocation();

    setSubmitting(true);
    setError(null);
    try {
      const { imageUrl, thumbnailUrl } = await uploadApi.image(token, imageFile);
      const postData = {
        imageUrl,
        thumbnailUrl,
        caption: caption.trim(),
        category,
        cuisine: category === "food" ? cuisine : null,
        latitude: location.latitude,
        longitude: location.longitude,
        worthIt,
        wouldReturn,
        locationName: "",
        countryCode: "",
        takenAt: exifData?.takenAt || null,
        exifData: exifData || { hasGps: false, device: "Unknown" },
      };
      await postsApi.create(token, postData);
      hapticSuccess();
      setSubmitted(true);
    } catch (err) {
      setError(err.message || t("upload.failedCreate"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
    setImageFile(null);
    setCaption("");
    setCategory(null);
    setCuisine(null);
    setWorthIt(null);
    setWouldReturn(null);
    setSubmitted(false);
    setSubmitting(false);
    setError(null);
    setStep("select");
    reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!isAuthenticated) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-th-bg px-6">
        <div className="w-16 h-16 rounded-2xl bg-th-surface border border-th-border flex items-center justify-center mb-6">
          <Aperture size={28} className="text-th-accent" />
        </div>
        <h2 className="text-xl font-bold text-th-text">{t("upload.sharePrompt")}</h2>
        <p className="text-sm text-th-muted mt-2 text-center">
          {t("upload.loginPrompt")}
        </p>
        <button
          onClick={() => navigate("/login")}
          className="mt-6 px-8 py-3 bg-th-accent text-th-bg rounded-xl text-sm font-semibold"
        >
          {t("auth.login")}
        </button>
        <button
          onClick={() => navigate("/register")}
          className="mt-3 px-6 py-2 text-sm font-medium text-th-muted"
        >
          {t("auth.createAccount")}
        </button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-th-bg px-6">
        <div className="w-16 h-16 rounded-2xl bg-th-accent-dim border border-th-accent-border flex items-center justify-center mb-6">
          <Check size={28} className="text-th-accent" />
        </div>
        <h2 className="text-xl font-bold text-th-text">{t("upload.pinned")}</h2>
        <p className="text-sm text-th-muted mt-2 text-center">
          {t("upload.liveOnMap")}
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 px-8 py-3 bg-th-accent text-th-bg rounded-xl text-sm font-semibold"
        >
          {t("upload.viewOnMap")}
        </button>
        <button
          onClick={handleReset}
          className="mt-3 px-8 py-3 bg-th-surface border border-th-border text-th-text-sec rounded-xl text-sm font-semibold"
        >
          {t("upload.captureAnother")}
        </button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
      <div className="px-4 py-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <h1 className="text-xl font-bold text-th-text">{t("upload.title")}</h1>
        <p className="text-xs text-th-muted mt-1 font-mono uppercase tracking-wider">
          {t("upload.subtitle")}
        </p>
      </div>

      {step === "select" && (
        <div className="px-4 space-y-3">
          {isNative ? (
            <>
              <div
                onClick={() => handleNativePick("camera")}
                className="w-full h-36 border border-dashed border-th-border rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-th-text-sec active:bg-th-surface transition-all"
              >
                <Camera size={32} className="text-th-muted mb-3" />
                <p className="text-sm text-th-text-sec">{t("upload.takePhoto")}</p>
                <p className="text-xs text-th-muted mt-1 font-mono">
                  {t("upload.snapNow")}
                </p>
              </div>
              <div
                onClick={() => handleNativePick("gallery")}
                className="w-full h-36 border border-dashed border-th-border rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-th-text-sec active:bg-th-surface transition-all"
              >
                <Aperture size={32} className="text-th-muted mb-3" />
                <p className="text-sm text-th-text-sec">{t("upload.chooseGallery")}</p>
                <p className="text-xs text-th-muted mt-1 font-mono">
                  {t("upload.exifAuto")}
                </p>
              </div>
            </>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-48 border border-dashed border-th-border rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-th-text-sec active:bg-th-surface transition-all"
            >
              <Aperture size={32} className="text-th-muted mb-3" />
              <p className="text-sm text-th-text-sec">{t("upload.singlePhoto")}</p>
              <p className="text-xs text-th-muted mt-1 font-mono">
                {t("upload.exifAuto")}
              </p>
            </div>
          )}
          <div
            onClick={() => setStep("batch")}
            className="w-full h-32 border border-dashed border-th-border rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-th-text-sec active:bg-th-surface transition-all"
          >
            <Images size={32} className="text-th-muted mb-3" />
            <p className="text-sm text-th-text-sec">{t("upload.batchUpload")}</p>
            <p className="text-xs text-th-muted mt-1 font-mono">
              {t("upload.batchSubtitle")}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
        </div>
      )}

      {step === "batch" && (
        <BatchUploadView onBack={() => setStep("select")} />
      )}

      {step === "details" && preview && (
        <div className="px-4 space-y-4 pb-8">
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-full max-h-64 object-contain rounded-2xl bg-th-surface"
            />
            <button
              onClick={handleReset}
              className="absolute top-2 right-2 w-8 h-8 bg-black/60 backdrop-blur-sm text-white rounded-xl text-sm flex items-center justify-center border border-white/10"
            >
              <X size={16} />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-th-muted text-xs">
              <Loader size={12} className="animate-spin" />
              <span className="font-mono">{t("upload.readingExif")}</span>
            </div>
          ) : exifData ? (
            <div className="bg-th-surface rounded-xl p-3 text-xs space-y-2 border border-th-border">
              <div className="flex justify-between items-center">
                <span className="text-th-muted flex items-center gap-1.5">
                  <MapPin size={12} /> {t("upload.gps")}
                </span>
                <span className={`font-mono ${exifData.hasGps ? "text-th-accent" : "text-amber-500"}`}>
                  {exifData.hasGps ? t("upload.gpsLocked") : t("upload.gpsNotFound")}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-th-muted flex items-center gap-1.5">
                  <Cpu size={12} /> {t("upload.device")}
                </span>
                <span className="text-th-text-sec font-mono">{exifData.device}</span>
              </div>
              {exifData.originalTimestamp && (
                <div className="flex justify-between items-center">
                  <span className="text-th-muted flex items-center gap-1.5">
                    <Clock size={12} /> {t("upload.taken")}
                  </span>
                  <span className="text-th-text-sec font-mono">{exifData.originalTimestamp}</span>
                </div>
              )}
            </div>
          ) : null}

          {exifData && !exifData.hasGps && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-4">
              <p className="text-sm text-amber-500 font-semibold">{t("upload.noGpsTitle")}</p>
              <p className="text-xs text-amber-500/80 mt-1.5 leading-relaxed">{t("upload.noGpsMessage")}</p>
              <button
                onClick={() => { handleReset(); setStep("batch"); }}
                className="mt-3 w-full py-2.5 bg-amber-500 text-black rounded-xl text-sm font-semibold"
              >
                {t("upload.switchToBatch")}
              </button>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-th-text mb-2">{t("upload.category")}</p>
            <CategoryPicker categories={CATEGORIES} value={category} onChange={(c) => { setCategory(c); if (c !== "food") setCuisine(null); }} />
          </div>

          {category === "food" && (
            <div>
              <p className="text-sm font-medium text-th-text mb-2">{t("upload.cuisine")}</p>
              <CuisinePicker value={cuisine} onChange={setCuisine} />
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-th-text mb-2">{t("upload.worthIt")}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setWorthIt(true)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                  worthIt === true
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-500"
                    : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
                }`}
              >
                <ThumbsUp size={14} />
                {t("upload.worthItYes")}
              </button>
              <button
                onClick={() => setWorthIt(false)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                  worthIt === false
                    ? "bg-red-500/15 border-red-500/40 text-red-500"
                    : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
                }`}
              >
                <X size={14} />
                {t("upload.worthItNo")}
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-th-text mb-2">{t("upload.wouldReturn")}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setWouldReturn(true)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                  wouldReturn === true
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-500"
                    : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
                }`}
              >
                <RotateCcw size={14} />
                {t("upload.wouldReturnYes")}
              </button>
              <button
                onClick={() => setWouldReturn(false)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                  wouldReturn === false
                    ? "bg-red-500/15 border-red-500/40 text-red-500"
                    : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
                }`}
              >
                <X size={14} />
                {t("upload.wouldReturnNo")}
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-th-text mb-2">{t("upload.caption")}</p>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t("upload.captionPlaceholder")}
              maxLength={280}
              rows={3}
              className="w-full px-3 py-2.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted resize-none focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
            />
            <p className="text-xs text-th-muted text-right font-mono">{caption.length}/280</p>
          </div>

          {error && (
            <p className="text-sm text-th-danger bg-th-danger-bg border border-th-danger-border px-3 py-2 rounded-xl">{error}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={!hasLocation || !category || !caption.trim() || worthIt === null || wouldReturn === null || submitting}
            className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader size={14} className="animate-spin" />
                {t("upload.uploading")}
              </>
            ) : (
              t("upload.pinToMap")
            )}
          </button>
          {!submitting && (() => {
            const missing = [];
            if (!hasLocation) missing.push(t("upload.missingGps"));
            if (!category) missing.push(t("upload.missingCategory"));
            if (worthIt === null) missing.push(t("upload.missingWorthIt"));
            if (wouldReturn === null) missing.push(t("upload.missingWouldReturn"));
            if (!caption.trim()) missing.push(t("upload.missingCaption"));
            if (missing.length === 0) return null;
            return (
              <p className="text-xs text-th-muted text-center -mt-1">
                {t("upload.stillNeeded", { items: missing.join(" · ") })}
              </p>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default UploadView;
